// Strict JSONL → typed record parser (§7). Rejects unknown fields, malformed
// records, missing/invalid source spans, and inline-tag syntax errors. Never
// repairs or downgrades: a bad line fails the batch (§12.3).

import {
  type IngestRecord,
  type NameStateRef,
  type SourceSpan,
  type SpeakerRef,
  NON_ACTOR_SPEAKER_KINDS,
  RECORD_KINDS,
} from "./records.js";
import { normalizeSceneTime } from "./environment.js";

export class JsonlParseError extends Error {
  constructor(message: string, readonly line: number) {
    super(`line ${line}: ${message}`);
  }
}

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function requireKeys(obj: Obj, allowed: string[], line: number): void {
  for (const key of Object.keys(obj)) {
    if (!allowed.includes(key)) throw new JsonlParseError(`unknown field '${key}'`, line);
  }
}

function parseSource(v: unknown, line: number): SourceSpan {
  if (!isObj(v)) throw new JsonlParseError("missing source", line);
  requireKeys(v, ["chunk", "line_start", "line_end"], line);
  const { chunk, line_start, line_end } = v;
  if (![chunk, line_start, line_end].every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0)) {
    throw new JsonlParseError("source needs integer chunk/line_start/line_end", line);
  }
  if ((line_start as number) >= (line_end as number)) throw new JsonlParseError("source line_start must be < line_end", line);
  return { chunk: chunk as number, line_start: line_start as number, line_end: line_end as number };
}

function str(obj: Obj, key: string, line: number): string {
  const v = obj[key];
  if (typeof v !== "string" || v === "") throw new JsonlParseError(`'${key}' must be a non-empty string`, line);
  return v;
}

function optStr(obj: Obj, key: string, line: number): string | undefined {
  if (!(key in obj) || obj[key] === undefined) return undefined;
  const v = obj[key];
  if (typeof v !== "string" || v === "") throw new JsonlParseError(`'${key}' must be a non-empty string`, line);
  return v;
}

function parseEntityRefs(v: unknown, key: string, line: number): NameStateRef[] {
  if (!Array.isArray(v)) throw new JsonlParseError(`'${key}' must be an array`, line);
  return v.map((item) => {
    if (!isObj(item)) throw new JsonlParseError(`'${key}' entries must be objects`, line);
    requireKeys(item, ["name", "state"], line);
    const ref: NameStateRef = { name: str(item, "name", line) };
    const state = optStr(item, "state", line);
    if (state !== undefined) ref.state = state;
    return ref;
  });
}

function parseSpeaker(v: unknown, line: number): SpeakerRef {
  if (!isObj(v)) throw new JsonlParseError("dialogue/inner_thought needs a speaker", line);
  if ("kind" in v) {
    requireKeys(v, ["kind", "label"], line);
    const kind = v.kind;
    if (typeof kind !== "string" || !(NON_ACTOR_SPEAKER_KINDS as readonly string[]).includes(kind)) {
      throw new JsonlParseError(`speaker.kind must be one of ${NON_ACTOR_SPEAKER_KINDS.join("|")}`, line);
    }
    const ref: SpeakerRef = { kind: kind as (typeof NON_ACTOR_SPEAKER_KINDS)[number] };
    const label = optStr(v, "label", line);
    if (label !== undefined) ref.label = label;
    return ref;
  }
  requireKeys(v, ["name", "state"], line);
  const name = str(v, "name", line);
  if (!/[\p{L}\p{N}]/u.test(name)) {
    throw new JsonlParseError("speaker.name must contain a letter or number", line);
  }
  const ref: SpeakerRef = { name };
  const state = optStr(v, "state", line);
  if (state !== undefined) ref.state = state;
  return ref;
}

function parseRecord(obj: Obj, line: number): IngestRecord {
  const kind = obj.kind;
  if (typeof kind !== "string" || !(RECORD_KINDS as readonly string[]).includes(kind)) {
    throw new JsonlParseError(`unknown or missing kind '${String(kind)}'`, line);
  }
  const source = parseSource(obj.source, line);

  switch (kind) {
    case "episode": {
      requireKeys(obj, ["kind", "episode", "title", "source"], line);
      const episode = obj.episode;
      if (typeof episode !== "number" || !Number.isInteger(episode)) throw new JsonlParseError("episode must be an integer", line);
      const rec = { kind, episode, source } as IngestRecord;
      const title = optStr(obj, "title", line);
      if (title !== undefined) (rec as { title?: string }).title = title;
      return rec;
    }
    case "scene": {
      requireKeys(obj, ["kind", "scene", "environment", "location", "actors", "props", "source"], line);
      const rec: Record<string, unknown> = { kind, source };
      if ("scene" in obj && obj.scene !== undefined) {
        if (typeof obj.scene !== "number" || !Number.isInteger(obj.scene)) throw new JsonlParseError("scene must be an integer", line);
        rec.scene = obj.scene;
      }
      if ("environment" in obj && obj.environment !== undefined) {
        if (!isObj(obj.environment)) throw new JsonlParseError("environment must be an object", line);
        requireKeys(obj.environment, ["space", "time"], line);
        const environment: { space?: string; time?: string } = {};
        const space = optStr(obj.environment, "space", line);
        if (space !== undefined) environment.space = space;
        const rawTime = optStr(obj.environment, "time", line);
        if (rawTime !== undefined) {
          const time = normalizeSceneTime(rawTime);
          if (!time) {
            throw new JsonlParseError(
              "environment.time must be day|night|dawn|dusk or a recognized Chinese equivalent",
              line,
            );
          }
          environment.time = time;
        }
        rec.environment = environment;
      }
      if ("location" in obj && obj.location !== undefined) {
        if (!isObj(obj.location)) throw new JsonlParseError("location must be an object", line);
        requireKeys(obj.location, ["name", "state"], line);
        const loc: Record<string, unknown> = { name: str(obj.location, "name", line) };
        const st = optStr(obj.location, "state", line);
        if (st !== undefined) loc.state = st;
        rec.location = loc;
      }
      if ("actors" in obj && obj.actors !== undefined) rec.actors = parseEntityRefs(obj.actors, "actors", line);
      if ("props" in obj && obj.props !== undefined) rec.props = parseEntityRefs(obj.props, "props", line);
      return rec as unknown as IngestRecord;
    }
    case "action":
    case "transition": {
      requireKeys(obj, ["kind", "content", "source"], line);
      const content = str(obj, "content", line);
      return { kind, content, source } as IngestRecord;
    }
    case "dialogue":
    case "inner_thought": {
      requireKeys(obj, ["kind", "speaker", "content", "emotion", "source"], line);
      const content = str(obj, "content", line);
      const speaker = parseSpeaker(obj.speaker, line);
      const rec: Record<string, unknown> = { kind, speaker, content, source };
      const emotion = optStr(obj, "emotion", line);
      if (emotion !== undefined) rec.emotion = emotion;
      return rec as unknown as IngestRecord;
    }
    case "note": {
      requireKeys(obj, ["kind", "content", "category", "source"], line);
      const content = str(obj, "content", line);
      const rec: Record<string, unknown> = { kind, content, source };
      if ("category" in obj && obj.category !== undefined) {
        const cat = obj.category;
        if (cat !== "author_note" && cat !== "format_note" && cat !== "unclassified") {
          throw new JsonlParseError("note.category must be author_note|format_note|unclassified", line);
        }
        rec.category = cat;
      }
      return rec as unknown as IngestRecord;
    }
    default:
      throw new JsonlParseError(`unhandled kind '${kind}'`, line);
  }
}

// Parse full JSONL text. Blank lines are skipped; every other line must be a
// valid record. `lineOffset` lets callers report absolute file lines.
export function parseJsonl(text: string, lineOffset = 0): IngestRecord[] {
  const out: IngestRecord[] = [];
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!.trim();
    if (raw === "") continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new JsonlParseError("invalid JSON", i + 1 + lineOffset);
    }
    if (!isObj(parsed)) throw new JsonlParseError("record must be a JSON object", i + 1 + lineOffset);
    out.push(parseRecord(parsed, i + 1 + lineOffset));
  }
  return out;
}
